// Premium 1080x1920 share card (Instagram Stories ratio).
export type ShareCardData = {
  restaurantName: string;
  restaurantSlug?: string;
  photoUrl?: string | null;
  cuisineEmoji?: string;
  cuisineName?: string;
  neighbourhood?: string;
  city?: string;
  badges?: { name: string; colour: string }[];
  username: string;
};

const W = 1080;
const H = 1920;

let outfitLoaded = false;
async function ensureOutfit() {
  if (outfitLoaded) return;
  try {
    if ((document as any).fonts?.load) {
      await Promise.all([
        (document as any).fonts.load("400 24px Outfit"),
        (document as any).fonts.load("500 24px Outfit"),
        (document as any).fonts.load("600 32px Outfit"),
        (document as any).fonts.load("700 56px Outfit"),
        (document as any).fonts.load("800 56px Outfit"),
      ]);
    }
  } catch {}
  outfitLoaded = true;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function wrap(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

async function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawNoise(ctx: CanvasRenderingContext2D) {
  // Subtle noise via random pixel speckle on an offscreen canvas, scaled up.
  const n = document.createElement("canvas");
  n.width = 200;
  n.height = 200;
  const nctx = n.getContext("2d");
  if (!nctx) return;
  const img = nctx.createImageData(200, 200);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.random() * 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 10; // ~4% opacity
  }
  nctx.putImageData(img, 0, 0);
  const pattern = ctx.createPattern(n, "repeat");
  if (pattern) {
    ctx.save();
    ctx.globalAlpha = 0.04;
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
}

export async function generateShareCard(data: ShareCardData): Promise<Blob | null> {
  await ensureOutfit();
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#0A2540");
  grad.addColorStop(1, "#0D1B2E");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  drawNoise(ctx);

  // Top wordmark + tagline
  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = "700 48px Outfit, sans-serif";
  ctx.fillText("Forkd", 80, 140);
  ctx.fillStyle = "#94A3B8";
  ctx.font = "400 24px Outfit, sans-serif";
  ctx.fillText("let's fork things up.", 80, 180);

  // Centre photo / emoji
  const cx = 80;
  const cy = 300;
  const cw = W - 160; // 920
  const ch = Math.round((cw * 3) / 4); // 690 (4:3)
  let img: HTMLImageElement | null = null;
  if (data.photoUrl) img = await loadImage(data.photoUrl);

  if (img) {
    ctx.save();
    roundRect(ctx, cx, cy, cw, ch, 32);
    ctx.clip();
    // cover fit
    const ratio = Math.max(cw / img.width, ch / img.height);
    const iw = img.width * ratio;
    const ih = img.height * ratio;
    ctx.drawImage(img, cx + (cw - iw) / 2, cy + (ch - ih) / 2, iw, ih);
    ctx.restore();
  } else {
    ctx.fillStyle = "#1A3A5C";
    roundRect(ctx, cx, cy, cw, ch, 32);
    ctx.fill();
    ctx.font = "400 200px Outfit, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(data.cuisineEmoji ?? "🍽️", W / 2, cy + ch / 2 + 10);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
  }

  // Restaurant info
  let y = cy + ch + 70;
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 56px Outfit, sans-serif";
  ctx.textAlign = "left";
  const nameLines =
    data.restaurantName.length > 20
      ? wrap(ctx, data.restaurantName, cw)
      : [data.restaurantName];
  for (const line of nameLines.slice(0, 2)) {
    ctx.fillText(line, 80, y);
    y += 64;
  }
  y += 8;

  if (data.cuisineName) {
    ctx.fillStyle = "#94A3B8";
    ctx.font = "400 32px Outfit, sans-serif";
    const label = `${data.cuisineEmoji ?? ""} ${data.cuisineName}`.trim();
    ctx.fillText(label, 80, y);
    y += 42;
  }

  const place = [data.neighbourhood, data.city].filter(Boolean).join(" · ");
  if (place) {
    ctx.fillStyle = "#64748B";
    ctx.font = "400 28px Outfit, sans-serif";
    ctx.fillText(place, 80, y);
    y += 40;
  }

  // Badges
  const badges = (data.badges ?? []).slice(0, 3);
  if (badges.length) {
    y += 24;
    ctx.font = "500 24px Outfit, sans-serif";
    let bx = 80;
    for (const b of badges) {
      const text = `★ ${b.name}`;
      const tw = ctx.measureText(text).width;
      const padX = 24;
      const pw = tw + padX * 2;
      const ph = 56;
      // 20% opacity fill (hex + alpha)
      ctx.fillStyle = b.colour + "33";
      roundRect(ctx, bx, y, pw, ph, 28);
      ctx.fill();
      ctx.strokeStyle = b.colour;
      ctx.lineWidth = 1;
      roundRect(ctx, bx, y, pw, ph, 28);
      ctx.stroke();
      ctx.fillStyle = b.colour;
      ctx.textBaseline = "middle";
      ctx.fillText(text, bx + padX, y + ph / 2 + 1);
      ctx.textBaseline = "alphabetic";
      bx += pw + 14;
      if (bx > W - 200) break;
    }
  }

  // Bottom: rule + username + QR placeholder
  const ruleY = 1700;
  ctx.strokeStyle = "#1E3A5C";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, ruleY);
  ctx.lineTo(W - 80, ruleY);
  ctx.stroke();

  ctx.fillStyle = "#94A3B8";
  ctx.font = "500 28px Outfit, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(`@${data.username}`, 80, 1770);

  // QR placeholder
  const qrSize = 140;
  const qrX = W - 80 - qrSize;
  const qrY = 1700 + 30;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, qrX, qrY, qrSize, qrSize, 18);
  ctx.fill();
  ctx.fillStyle = "#0A2540";
  ctx.font = "600 20px Outfit, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("forkd.app", qrX + qrSize / 2, qrY + qrSize / 2);

  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/png");
  });
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "forkd";
}

export async function shareCard(data: ShareCardData) {
  const blob = await generateShareCard(data);
  if (!blob) return;
  const date = new Date().toISOString().slice(0, 10);
  const slug = data.restaurantSlug ?? slugify(data.restaurantName);
  const filename = `forkd-${slug}-${date}.png`;
  const file = new File([blob], filename, { type: "image/png" });

  const navAny = navigator as any;
  if (navAny.canShare && navAny.canShare({ files: [file] }) && navAny.share) {
    try {
      await navAny.share({
        files: [file],
        title: "Forkd",
        text: `I Forkd it 🍴 Just ate at ${data.restaurantName}. Come find me on Forkd.`,
      });
      return;
    } catch {
      // fall through to download
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
